import { useState, useEffect } from "react";
import { Box } from "@mui/material";
import CommonModal from "@/modules/common/components/ui-kit/modals/common-modal";
import MobileModal from "@/modules/common/components/ui-kit/modals/mobile-modal";
import { NwButton } from "@nawy/ui";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";
import QrCodeSkeleton from "@/modules/lead-page/new/components/header/actions/generate-qr-code/qr-code-skeleton";
import { QrCodePreviewModalProps } from "@/modules/lead-page/new/components/header/types";
import { nawyTheme } from "@/styles/nawy-theme/nawy-theme";

const formatImageUrl = (url: string): string => {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `https://${url}`;
};

const QrCodePreviewModal = ({
  open,
  onClose,
  data,
  onRegenerate,
  onSend,
  isRegenerating,
  isSending,
}: QrCodePreviewModalProps) => {
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const { isMobile } = useScreenSize();

  useEffect(() => {
    if (!data?.url) return;
    setIsImageLoaded(false);
  }, [data?.url]);

  const showSkeleton = !data?.url || isRegenerating || !isImageLoaded;

  const imageContent = (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        padding: isMobile ? 0 : "16px",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {showSkeleton && <QrCodeSkeleton isMobile={isMobile} />}
      {data?.url && (
        <img
          src={formatImageUrl(data.url)}
          alt="QR Code Preview"
          onLoad={() => setIsImageLoaded(true)}
          style={{
            width: isMobile ? "100%" : "200px",
            objectFit: "contain",
            borderRadius: "16px",
            display: showSkeleton ? "none" : "block",
          }}
        />
      )}
    </Box>
  );

  if (isMobile) {
    return (
      <MobileModal
        open={open}
        onClose={onClose}
        title="Generate QR code"
        extraButtonText="Regenerate"
        onExtra={onRegenerate}
        confirmButtonText="Send to client"
        onConfirm={onSend}
        isConfirmDisabled={!data?.url || isSending}
        isLoading={isSending}
        snapPoints={[1]}
      >
        {imageContent}
      </MobileModal>
    );
  }

  return (
    <CommonModal
      open={open}
      onClose={onClose}
      title="Generate QR code"
      sx={{ width: "480px" }}
    >
      {imageContent}
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "flex-end",
          gap: nawyTheme.nawy.spacing.sm,
          padding: `0 ${nawyTheme.nawy.spacing.md} ${nawyTheme.nawy.spacing.md}`,
        }}
      >
        <NwButton
          variant="outlined"
          onClick={onRegenerate}
          disabled={isRegenerating || isSending}
        >
          Regenerate
        </NwButton>
        <NwButton
          onClick={onSend}
          disabled={!data?.url || isSending}
          loading={isSending}
        >
          Send to client
        </NwButton>
      </Box>
    </CommonModal>
  );
};

export default QrCodePreviewModal;
